import {
    createAsyncThunk,
    createSlice
} from '@reduxjs/toolkit';
import apiService from '../../Services/apiServices';
const STORAGE_KEY = 'ifit_auth';
const parseJwtPayload = (token) => {
    try {
        if (!token || token.split('.').length < 2) {
            return null;
        }
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
        return JSON.parse(atob(padded));
    } catch (e) {
        return null;
    }
};
const isTokenExpired = (tokenPayload) => {
    const exp = tokenPayload?.exp;
    if (!exp) {
        return false;
    }
    const nowSeconds = Math.floor(Date.now() / 1000);
    return exp <= nowSeconds;
};
const readStorage = () => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        const parsed = raw ? JSON.parse(raw) : null;
        if (!parsed?.access_token) {
            return parsed;
        }
        const payload = parseJwtPayload(parsed.access_token);
        if (payload && isTokenExpired(payload)) {
            localStorage.removeItem(STORAGE_KEY);
            return null;
        }
        return parsed;
    } catch (e) {
        return null;
    }
};
const writeStorage = (payload) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
};
const clearStorage = () => {
    localStorage.removeItem(STORAGE_KEY);
};
const roleAliases = {
    partner: ['partner'],
    superadmin: ['superadmin', 'super_admin', 'admin']
};
const roleMatches = (selectedRole, roles = []) => {
    const normalized = roles.map((role) => String(role).toLowerCase());
    return roleAliases[selectedRole]?.some((role) => normalized.includes(role));
};
const persisted = readStorage();
const initialState = {
    access_token: persisted?.access_token || null,
    token_type: persisted?.token_type || null,
    roles: persisted?.roles || [],
    user_id: persisted?.user_id || null,
    name: persisted?.name || '',
    email: persisted?.email || '',
    mobile: persisted?.mobile || '',
    selectedRole: 'partner',
    otpRequested: false,
    otpMedium: null,
    status: 'idle',
    error: null,
    resetStatus: 'idle',
    resetError: null,
    resetRequested: false
};
export const requestLoginOtp = createAsyncThunk('auth/requestLoginOtp', async ({
    identifier,
    role
}, thunkAPI) => {
    try {
        const response = await apiService.post('/auth/login/otp/request', {
            identifier,
            role
        });
        return response.data;
    } catch (error) {
        return thunkAPI.rejectWithValue(error?.response?.data?.detail || 'Unable to send OTP');
    }
});
export const login = createAsyncThunk('auth/login', async ({
    identifier,
    otp,
    role
}, thunkAPI) => {
    try {
        const response = await apiService.post('/auth/login/otp/verify', {
            identifier,
            otp,
            role
        });
        const payload = response.data;
        if (!roleMatches(role, payload?.roles || [])) {
            return thunkAPI.rejectWithValue(`This account is not authorized as ${role}.`);
        }
        writeStorage(payload);
        return payload;
    } catch (error) {
        return thunkAPI.rejectWithValue(error?.response?.data?.detail || 'Invalid credentials');
    }
});
export const loginWithPassword = createAsyncThunk('auth/loginWithPassword', async ({
    email,
    password,
    role
}, thunkAPI) => {
    try {
        const response = await apiService.post('/auth/login', {
            email,
            password
        });
        const payload = response.data;
        if (role && !roleMatches(role, payload?.roles || [])) {
            return thunkAPI.rejectWithValue(`This account is not authorized as ${role}.`);
        }
        writeStorage(payload);
        return payload;
    } catch (error) {
        return thunkAPI.rejectWithValue(error?.response?.data?.detail || 'Invalid credentials');
    }
});
export const requestPasswordResetOtp = createAsyncThunk('auth/requestPasswordResetOtp', async ({
    identifier,
    role
}, thunkAPI) => {
    try {
        const response = await apiService.post('/auth/password/forgot/request', {
            identifier,
            role
        });
        return response.data;
    } catch (error) {
        return thunkAPI.rejectWithValue(error?.response?.data?.detail || 'Unable to send OTP');
    }
});
export const verifyPasswordResetOtp = createAsyncThunk('auth/verifyPasswordResetOtp', async ({
    identifier,
    otp,
    new_password,
    role
}, thunkAPI) => {
    try {
        const response = await apiService.post('/auth/password/forgot/verify', {
            identifier,
            otp,
            new_password,
            role
        });
        return response.data;
    } catch (error) {
        return thunkAPI.rejectWithValue(error?.response?.data?.detail || 'Unable to reset password');
    }
});
const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        setSelectedRole: (state, action) => {
            state.selectedRole = action.payload;
        },
        clearOtpState: (state) => {
            state.otpRequested = false;
            state.otpMedium = null;
        },
        clearResetState: (state) => {
            state.resetRequested = false;
            state.resetStatus = 'idle';
            state.resetError = null;
        },
        logout: (state) => {
            state.access_token = null;
            state.token_type = null;
            state.roles = [];
            state.user_id = null;
            state.name = '';
            state.email = '';
            state.mobile = '';
            state.otpRequested = false;
            state.otpMedium = null;
            state.error = null;
            state.status = 'idle';
            state.resetRequested = false;
            state.resetStatus = 'idle';
            state.resetError = null;
            clearStorage();
        }
    },
    extraReducers: (builder) => {
        builder.addCase(requestLoginOtp.pending, (state) => {
            state.status = 'loading';
            state.error = null;
        }).addCase(requestLoginOtp.fulfilled, (state, action) => {
            state.status = 'succeeded';
            state.otpRequested = true;
            state.otpMedium = action.payload?.medium || null;
        }).addCase(requestLoginOtp.rejected, (state, action) => {
            state.status = 'failed';
            state.error = action.payload || 'Unable to send OTP';
        }).addCase(login.pending, (state) => {
            state.status = 'loading';
            state.error = null;
        }).addCase(login.fulfilled, (state, action) => {
            state.status = 'succeeded';
            state.access_token = action.payload.access_token;
            state.token_type = action.payload.token_type;
            state.roles = action.payload.roles || [];
            state.user_id = action.payload.user_id || null;
            state.name = action.payload.name || '';
            state.email = action.payload.email || '';
            state.mobile = action.payload.mobile || '';
            state.otpRequested = false;
            state.otpMedium = null;
        }).addCase(login.rejected, (state, action) => {
            state.status = 'failed';
            state.error = action.payload || 'Unable to login';
        }).addCase(loginWithPassword.pending, (state) => {
            state.status = 'loading';
            state.error = null;
        }).addCase(loginWithPassword.fulfilled, (state, action) => {
            state.status = 'succeeded';
            state.access_token = action.payload.access_token;
            state.token_type = action.payload.token_type;
            state.roles = action.payload.roles || [];
            state.user_id = action.payload.user_id || null;
            state.name = action.payload.name || '';
            state.email = action.payload.email || '';
            state.mobile = action.payload.mobile || '';
            state.otpRequested = false;
            state.otpMedium = null;
        }).addCase(loginWithPassword.rejected, (state, action) => {
            state.status = 'failed';
            state.error = action.payload || 'Unable to login';
        }).addCase(requestPasswordResetOtp.pending, (state) => {
            state.resetStatus = 'loading';
            state.resetError = null;
        }).addCase(requestPasswordResetOtp.fulfilled, (state) => {
            state.resetStatus = 'succeeded';
            state.resetRequested = true;
        }).addCase(requestPasswordResetOtp.rejected, (state, action) => {
            state.resetStatus = 'failed';
            state.resetError = action.payload || 'Unable to send OTP';
        }).addCase(verifyPasswordResetOtp.pending, (state) => {
            state.resetStatus = 'loading';
            state.resetError = null;
        }).addCase(verifyPasswordResetOtp.fulfilled, (state) => {
            state.resetStatus = 'succeeded';
            state.resetRequested = false;
        }).addCase(verifyPasswordResetOtp.rejected, (state, action) => {
            state.resetStatus = 'failed';
            state.resetError = action.payload || 'Unable to reset password';
        });
    }
});
export const {
    setSelectedRole,
    clearOtpState,
    clearResetState,
    logout
} = authSlice.actions;
export default authSlice.reducer;